import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Purchases, PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor';
import { posthog } from 'posthog-js';
import { toast } from 'react-toastify';
import * as stores from '../../stores';
import { entitlementIdentifier } from '../../stores';
import { completeOnboarding } from '../../utils/onboarding';
import { trackOnboardingCompletion } from '../../utils/analytics';
import { setInvestorPersona } from '../MorePage/InvestorPersonaSelector';
import { InvestorType } from '../../utils/smartChatPrompts';
import { OneTimeOfferScreen } from '../PaymentModalByPreference/OneTimeOfferScreen';
import {
  FALLBACK_PRICES,
  findDiscountPackage,
  findPackageInAllOfferings,
  getOfferTrialDays,
} from '../../utils/subscriptionUtils';
import { appsflyerEvents } from '../../utils/appsflyerEvents';
import { reportError } from '../../utils/reporting';
import { UserSubscriptionService, SubscriptionType } from '../../services/userSubscriptionService';

export const OneTimeOfferPage: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const offerings = stores.Purchase.useState((s) => s.offerings);
  const [isLoading, setIsLoading] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  // offerings is null/undefined while still loading asynchronously; an empty-but-loaded
  // object means RevenueCat returned no packages for this user.
  const offeringsLoaded = offerings !== null && offerings !== undefined;

  // Compute discount package (same pattern as PaymentModalByPreference)
  const earlyTargetOffering = offerings?.all?.['weekly_yearly_pricing'] || offerings?.current;
  const oneTimeDiscountOffering = offerings?.all?.['one_time_discount'];
  const discountPackage =
    oneTimeDiscountOffering?.annual ||
    oneTimeDiscountOffering?.availablePackages?.find((p) => p.packageType === 'ANNUAL') ||
    findDiscountPackage(earlyTargetOffering) ||
    null;

  // Get annual package for original price
  const annualPackage = findPackageInAllOfferings(offerings ?? null, 'ANNUAL');

  // On native, only show the OTO when a discount package is actually available — otherwise
  // the UI would display a "50% OFF" badge while the App Store charges full price.
  // This mirrors PaymentModalByPreference's `canShowOneTimeOffer = !isNative || !!discountPackage`.
  const canShowOneTimeOffer = !isNative || !!discountPackage;

  const originalAnnualPrice = annualPackage?.product?.price ?? FALLBACK_PRICES.ANNUAL;
  const discountAnnualPrice = discountPackage?.product?.price ?? FALLBACK_PRICES.DISCOUNT_ANNUAL;
  const currencySymbol = discountPackage?.product?.priceString?.[0] ?? annualPackage?.product?.priceString?.[0] ?? '$';

  // Derive free trial days from the package intro price via shared utility (avoids duplicating
  // the same IIFE that already exists in PaymentModalByPreference).
  const offerTrialDays = getOfferTrialDays(discountPackage || annualPackage);

  // Only fire the viewed event once we've confirmed the offer is actually being shown.
  // Firing on raw mount would double-count native users who are immediately redirected
  // away when offerings load with no discount package available.
  useEffect(() => {
    if (offeringsLoaded && canShowOneTimeOffer) {
      posthog.capture('onboarding_oto_viewed');
    }
  }, [offeringsLoaded, canShowOneTimeOffer]);

  // If offerings have loaded on native and no discount package is available, redirect to chat
  // immediately to avoid showing a "50% OFF" badge that conflicts with the App Store price.
  useEffect(() => {
    if (offeringsLoaded && !canShowOneTimeOffer) {
      handleSkipToChat('onboarding_oto_no_offer_redirected', 'no_offer_available');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeringsLoaded, canShowOneTimeOffer]);

  const handleSkipToChat = (
    eventName: string,
    trigger: 'close_button' | 'explore_free_button' | 'no_package_fallback' | 'no_offer_available',
  ) => {
    posthog.capture(eventName, { trigger });
    localStorage.setItem('oneTimeDiscountOffered', 'true');
    localStorage.removeItem('onboardingIndex');

    // Track onboarding completion analytics before clearing localStorage data.
    // The explore-free / dismiss path completes 0 onboarding steps (skip user).
    const motivations: string[] = (() => {
      try {
        return JSON.parse(localStorage.getItem('onboardingMotivations') || '[]');
      } catch {
        return [];
      }
    })();
    const watchlist = stores.General.getRawState().watchlist || {};
    const selectedSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);
    const startTime = parseInt(localStorage.getItem('onboardingStartTime') || '0', 10);
    const completionTime = startTime > 0 ? Date.now() - startTime : 0;
    trackOnboardingCompletion({
      totalSteps: 0,
      selectedMotivations: motivations,
      selectedSymbols,
      completionTime,
    });

    localStorage.removeItem('onboardingMotivations');
    localStorage.removeItem('onboardingStartTime');
    completeOnboarding();
    history.push('/chat');
  };

  const handleClose = () => handleSkipToChat('onboarding_oto_dismissed', 'close_button');
  const handleExploreFree = () => handleSkipToChat('onboarding_oto_explore_free', 'explore_free_button');

  const handleRedeemOffer = async () => {
    posthog.capture('onboarding_oto_redeem_clicked');
    setIsLoading(true);

    try {
      if (Capacitor.isNativePlatform()) {
        const packageToPurchase = discountPackage || annualPackage;
        if (!packageToPurchase) {
          if (!offeringsLoaded) {
            // Offerings not yet fetched — show spinner and wait rather than treating
            // a temporarily-empty store as a terminal "no package" state.
            setIsLoading(false);
            return;
          }
          // Offerings loaded but no package found — fire a distinct event so funnel
          // analytics can differentiate system-forced fallbacks from voluntary
          // "Explore free" taps.
          handleSkipToChat('onboarding_oto_redeem_no_package', 'no_package_fallback');
          return;
        }

        const { customerInfo } = await Purchases.purchasePackage({ aPackage: packageToPurchase });

        if (customerInfo.entitlements.active[entitlementIdentifier]) {
          stores.Purchase.update((s) => {
            s.purchaserInfo = customerInfo;
          });

          // Track subscription purchase in AppsFlyer and PostHog
          try {
            const packagePrice = packageToPurchase.product.price ?? 0;
            const packageCurrency = packageToPurchase.product.currencyCode ?? 'USD';
            const planName = 'Annual Plan';
            const entitlement = customerInfo.entitlements.active[entitlementIdentifier];

            if (entitlement?.periodType === 'TRIAL') {
              await appsflyerEvents.subscription.trialStarted(planName, offerTrialDays, packagePrice, packageCurrency);
              console.log('[APPSFLYER] Trial started tracked successfully');
            }

            await appsflyerEvents.subscription.purchased(planName, packagePrice, packageCurrency);
            console.log('[APPSFLYER] Subscription purchase tracked successfully');

            posthog.capture('User Subscribed', {
              plan: planName,
              price: packagePrice,
              currency: packageCurrency,
              periodType: entitlement?.periodType,
              packageIdentifier: packageToPurchase.identifier,
            });
          } catch (trackingErr) {
            // Business-critical: Report revenue tracking failures to Sentry
            const trackingError = new Error(`Failed to track subscription purchase: ${trackingErr}`);
            reportError(trackingError);
          }

          try {
            const subscriptionService = UserSubscriptionService.getInstance();
            const subscriptionResult = await subscriptionService.registerSubscription(
              SubscriptionType.free,
              offerTrialDays,
            );
            if (!subscriptionResult.success) {
              toast.error(t('subscription.errorRegistering', { message: subscriptionResult.message }));
            }
          } catch (registrationErr) {
            console.error('[OneTimeOfferPage] Backend subscription registration failed:', registrationErr);
          }

          // Read and process onboarding analytics data before navigating away.
          // The unmount cleanup previously wiped these keys before PurchaseResult
          // could read them, causing setInvestorPersona to be skipped and
          // trackOnboardingCompletion to record zero time and no motivations.
          // We handle them explicitly here (mirroring handleSkipToChat) so the
          // unmount cleanup is no longer needed.
          // We also set wasOnboardingCompleted so completeOnboardingAndCleanup in
          // Result.tsx skips the analytics block — preventing double-tracking.
          const purchaseMotivations: InvestorType[] = (() => {
            try {
              return JSON.parse(localStorage.getItem('onboardingMotivations') || '[]');
            } catch {
              return [];
            }
          })();
          const purchaseWatchlist = stores.General.getRawState().watchlist || {};
          const purchaseSymbols = Object.keys(purchaseWatchlist).filter((s) => purchaseWatchlist[s]);
          const purchaseStartTime = parseInt(localStorage.getItem('onboardingStartTime') || '0', 10);
          const purchaseCompletionTime = purchaseStartTime > 0 ? Date.now() - purchaseStartTime : 0;
          trackOnboardingCompletion({
            totalSteps: 2,
            selectedMotivations: purchaseMotivations,
            selectedSymbols: purchaseSymbols,
            completionTime: purchaseCompletionTime,
          });
          if (purchaseMotivations.length > 0) {
            setInvestorPersona(purchaseMotivations[0]);
          }
          localStorage.removeItem('onboardingMotivations');
          localStorage.removeItem('onboardingStartTime');
          // Signal to completeOnboardingAndCleanup that analytics were already recorded.
          localStorage.setItem('wasOnboardingCompleted', 'true');

          localStorage.removeItem('hasCompletedOnboarding');
          localStorage.setItem('hasSubscribed', 'true');
          // 2 steps: welcome screen + this OTO page — keeps analytics accurate for
          // skip-then-subscribe users (PurchaseResult falls back to 6 when this is absent).
          localStorage.setItem('onboardingCompletionTotalSteps', '2');
          history.push('/result');
        } else {
          throw new Error('Entitlement not active after purchase');
        }
      } else {
        // Web: simulate purchase for testing
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            localStorage.removeItem('hasCompletedOnboarding');
            localStorage.setItem('hasSubscribed', 'true');
            localStorage.setItem('onboardingCompletionTotalSteps', '2');
            history.push('/result');
            resolve();
          }, 1000);
        });
      }
    } catch (error: any) {
      if (error?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        // User cancelled the native purchase dialog — stay on OTO page so they can retry
        return;
      } else {
        // Real error (network failure, payment declined, etc.) — show toast and let user retry
        toast.error(t('subscription.error.default'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OneTimeOfferScreen
      onClose={handleClose}
      onRedeemOffer={handleRedeemOffer}
      onExploreFree={handleExploreFree}
      originalAnnualPrice={originalAnnualPrice}
      discountAnnualPrice={discountAnnualPrice}
      currencySymbol={currencySymbol}
      freeTrialDays={offerTrialDays}
      isLoading={isLoading}
      isRedeemDisabled={!offeringsLoaded}
      isSkipUser={true}
    />
  );
};
